import React, { useCallback } from "react";
import { connect } from "react-redux";

import { faExclamationCircle } from "@fortawesome/pro-solid-svg-icons/faExclamationCircle";
import { faExclamationTriangle } from "@fortawesome/pro-solid-svg-icons/faExclamationTriangle";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Col from "antd/es/col";
import Row from "antd/es/row";
import cn from "classnames";
import { useIntl } from "react-intl";
import { Link } from "react-router-dom";

import DuplicateReason from "@mapmycustomers/shared/enum/DuplicateReason";
import { EntityType, EntityTypeSupportingDataMerging } from "@mapmycustomers/shared/types/entity";
import { DuplicateEntity } from "@mapmycustomers/shared/types/entity/Duplicate";
import { Button } from "@mapmycustomers/ui";

import DuplicateCell from "@app/component/DuplicateCell";
import { showEntityView } from "@app/store/entityView/actions";
import { useExtendedAnalytics } from "@app/util/analytic/AnalyticsService";
import useEntityLinkGetter from "@app/util/contexts/useEntityLinkGetter";
import { getDuplicateReasonDisplayName } from "@app/util/ui";

import styles from "./DuplicatesTable.module.scss";

interface Props {
  duplicates: DuplicateEntity[];
  // it is expected that DuplicateEntity has `entity: EntityType` field when this prop is not defined. But takes precedence when defined
  entityType?: EntityTypeSupportingDataMerging;
  showAsLinks?: boolean;
  showEntityView: typeof showEntityView;
  showHeader?: boolean;
}

const DuplicatesTable = ({
  duplicates,
  entityType,
  showAsLinks,
  showEntityView,
  showHeader = true,
}: Props) => {
  const intl = useIntl();
  const getEntityLink = useEntityLinkGetter();

  const mergeWarningText =
    entityType === EntityType.COMPANY
      ? "Merge Warning on Create Company"
      : "Merge Warning on Create Person";
  const mergeWarningAnalyticIssuer = useExtendedAnalytics(mergeWarningText);

  const handlePreview = useCallback(
    (entity: DuplicateEntity) => {
      showEntityView({ entityId: entity.id, entityType: entityType ?? entity.entity });
    },
    [entityType, showEntityView]
  );

  return (
    <div className={styles.container}>
      {showHeader && (
        <>
          <FontAwesomeIcon className={styles.icon} icon={faExclamationTriangle} size="3x" />
          <div className={styles.title}>
            {intl.formatMessage({
              id: "createEditEntity.duplicates.title",
              defaultMessage: "Warning",
              description: "Title of duplicates field",
            })}
          </div>

          <div className={styles.subTitle}>
            {intl.formatMessage(
              {
                id: "createEditEntity.duplicates.subTitle",
                defaultMessage: "{icon} Record may already exist",
                description: "Subtitle of duplicates field",
              },
              {
                icon: <FontAwesomeIcon className={styles.icon} icon={faExclamationCircle} />,
              }
            )}
          </div>
        </>
      )}
      <div className={cn(styles.duplicateContainer, { [styles.withHeader]: showHeader })}>
        <Row align="middle" className={styles.row} justify="space-between">
          <Col xs={{ span: 8 }} md={{ span: 10 }} xl={{ span: 10 }} xxl={{ span: 12 }}>
            {intl.formatMessage({
              id: "createEditEntity.duplicates.table.column.record",
              defaultMessage: "Record",
              description: "Title of Record column",
            })}
          </Col>
          <Col xs={{ span: 8 }} md={{ span: 7 }} xl={{ span: 8 }} xxl={{ span: 7 }}>
            {intl.formatMessage({
              id: "createEditEntity.duplicates.table.column.matchingFields",
              defaultMessage: "Potential duplicate values",
              description: "Title of Potential duplicate values column",
            })}
          </Col>
          <Col xs={{ span: 8 }} md={{ span: 7 }} xl={{ span: 6 }} xxl={{ span: 5 }} />
        </Row>

        {duplicates.slice(0, 2).map((duplicate) => (
          <Row
            align="middle"
            className={cn(styles.row, styles.duplicateRow)}
            justify="space-between"
            key={duplicate.id}
          >
            <Col xs={{ span: 8 }} md={{ span: 10 }} xl={{ span: 12 }} xxl={{ span: 12 }}>
              <DuplicateCell duplicate={duplicate} showAsLinks={showAsLinks} />
            </Col>
            <Col xs={{ span: 8 }} md={{ span: 7 }} xl={{ span: 6 }} xxl={{ span: 7 }}>
              <div className={styles.fields}>
                {duplicate.matchingFields
                  ?.map((field: DuplicateReason) => getDuplicateReasonDisplayName(intl, field))
                  .join(", ")}
              </div>
            </Col>
            <Col xs={{ span: 8 }} md={{ span: 7 }} xl={{ span: 6 }} xxl={{ span: 5 }}>
              {(duplicate.hasAccess ?? true) ? (
                <>
                  {showAsLinks ? (
                    <Link
                      target="_blank"
                      to={getEntityLink(entityType ?? duplicate.entity, duplicate.id)}
                    >
                      <Button>
                        {intl.formatMessage({
                          id: "createEditEntity.duplicates.table.open",
                          defaultMessage: "Open Record",
                          description: "Title of Open Record action",
                        })}
                      </Button>
                    </Link>
                  ) : (
                    <Button
                      onClick={() => {
                        mergeWarningAnalyticIssuer?.clicked(["Open record"]);
                        handlePreview(duplicate);
                      }}
                    >
                      {intl.formatMessage({
                        id: "createEditEntity.duplicates.table.open",
                        defaultMessage: "Open Record",
                        description: "Title of Open Record action",
                      })}
                    </Button>
                  )}
                </>
              ) : null}
            </Col>
          </Row>
        ))}
      </div>
      {duplicates.length > 2 && (
        <Row className={styles.more} justify="end">
          <Col>
            {intl.formatMessage(
              {
                id: "createEditEntity.duplicates.more",
                defaultMessage: "... and {count} more",
                description: "More snippet of duplicates field",
              },
              { count: duplicates.length - 2 }
            )}
          </Col>
        </Row>
      )}
    </div>
  );
};

const mapDispatchToProps = {
  showEntityView,
};

export default connect(null, mapDispatchToProps)(DuplicatesTable);
